import { apiFetch, isApiMode } from './http';

// Client for /platform/organizations — centres and clubs, who holds which
// role in each, and which clubs a centre has linked to its courts.

export interface OrganizationSummary {
  id: string;
  slug: string;
  name: string;
  kind: 'CENTRE' | 'CLUB' | string;
  /** The PSP account payments land in, or null when not connected (#284). */
  stripeAccountId: string | null;
  memberCount: number;
  linkCount: number;
}

export interface OrganizationRoleRef {
  id: string;
  key: string;
  name: string;
}

export interface OrganizationMember {
  userId: string;
  displayName: string | null;
  email: string | null;
  status: string;
  roles: OrganizationRoleRef[];
}

export interface OrganizationLinkView {
  id: string;
  /** `proposed` waits for the club's yes on its own «Samarbeid» page (slice 4a). */
  status: 'proposed' | 'active' | 'suspended' | string;
  settlementModel: 'centre_invoice' | 'direct' | string;
  agreedCourtRateOre: number | null;
  /** When the club said yes on its own page; null for a proposal or a link made here. */
  acceptedAt: string | null;
  /** What the CENTRE gives the club at its courts — a LINK role, or null. */
  centreGrantsRole: OrganizationRoleRef | null;
  /** What the CLUB gives the centre in its activities — a LINK role, or null. */
  clubGrantsRole: OrganizationRoleRef | null;
  club?: { id: string; slug: string; name: string };
  centre?: { id: string; slug: string; name: string };
}

export interface OrganizationDetail {
  organization: {
    id: string;
    slug: string;
    name: string;
    kind: string;
    /** The PSP account payments land in, or null when not connected (#284). */
    stripeAccountId: string | null;
  };
  members: OrganizationMember[];
  links: { asCentre: OrganizationLinkView[]; asClub: OrganizationLinkView[] };
  availableRoles: (OrganizationRoleRef & { isSystem: boolean; ownerTenantId: string | null })[];
  linkRoles: OrganizationRoleRef[];
}

export type OrganizationsResult =
  | { state: 'no-api' }
  | { state: 'ok'; organizations: OrganizationSummary[] }
  | { state: 'forbidden' }
  | { state: 'error'; status: number | null };

export async function fetchOrganizations(): Promise<OrganizationsResult> {
  if (!isApiMode()) return { state: 'no-api' };
  try {
    const res = await apiFetch('/platform/organizations');
    if (res.status === 401 || res.status === 403) return { state: 'forbidden' };
    if (!res.ok) return { state: 'error', status: res.status };
    const body = (await res.json()) as { organizations: OrganizationSummary[] };
    return { state: 'ok', organizations: body.organizations };
  } catch {
    return { state: 'error', status: null };
  }
}

export type OrganizationDetailResult =
  | { state: 'no-api' }
  | { state: 'ok'; detail: OrganizationDetail }
  | { state: 'forbidden' }
  | { state: 'error'; status: number | null };

export async function fetchOrganization(id: string): Promise<OrganizationDetailResult> {
  if (!isApiMode()) return { state: 'no-api' };
  try {
    const res = await apiFetch(`/platform/organizations/${encodeURIComponent(id)}`);
    if (res.status === 401 || res.status === 403) return { state: 'forbidden' };
    if (!res.ok) return { state: 'error', status: res.status };
    return { state: 'ok', detail: (await res.json()) as OrganizationDetail };
  } catch {
    return { state: 'error', status: null };
  }
}

/** The outcome of a write. Refusals carry the server's reason code. */
export type WriteResult =
  | { state: 'ok' }
  | { state: 'forbidden' }
  | { state: 'refused'; reason: string }
  | { state: 'error'; status: number | null };

const JSON_HEADERS = { 'Content-Type': 'application/json' };

async function write(path: string, init: RequestInit): Promise<WriteResult> {
  if (!isApiMode()) return { state: 'error', status: null };
  try {
    const res = await apiFetch(path, init);
    if (res.status === 401 || res.status === 403) return { state: 'forbidden' };
    if (res.status === 400 || res.status === 404 || res.status === 409) {
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      return { state: 'refused', reason: body?.error ?? 'refused' };
    }
    if (!res.ok) return { state: 'error', status: res.status };
    return { state: 'ok' };
  } catch {
    return { state: 'error', status: null };
  }
}

export function createOrganization(input: {
  slug: string;
  name: string;
  kind: 'CENTRE' | 'CLUB';
}): Promise<WriteResult> {
  return write('/platform/organizations', {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify(input),
  });
}

export function grantOrganizationRole(
  organizationId: string,
  userId: string,
  roleId: string,
): Promise<WriteResult> {
  return write(`/platform/organizations/${encodeURIComponent(organizationId)}/members`, {
    method: 'POST',
    headers: JSON_HEADERS,
    body: JSON.stringify({ userId, roleId }),
  });
}

/**
 * What an invitation did — which the page has to say, because the two
 * outcomes are different sentences.
 *
 * `WriteResult` deliberately discards the body, and that is right for a grant
 * («it worked» is the whole answer). Here it is not: «an account was created
 * and a link sent» and «somebody who already had an account now has access»
 * are different things to tell an operator, and guessing wrong means telling
 * them to expect a mail that was never sent.
 */
export type InviteResult =
  | { state: 'ok'; created: boolean; mailed: boolean }
  | { state: 'forbidden' }
  | { state: 'refused'; reason: string }
  | { state: 'error'; status: number | null };

export async function inviteToOrganization(
  organizationId: string,
  input: { email: string; displayName?: string; roleId: string },
): Promise<InviteResult> {
  if (!isApiMode()) return { state: 'error', status: null };
  try {
    const res = await apiFetch(
      `/platform/organizations/${encodeURIComponent(organizationId)}/invitations`,
      { method: 'POST', headers: JSON_HEADERS, body: JSON.stringify(input) },
    );
    if (res.status === 401 || res.status === 403) return { state: 'forbidden' };
    if (res.status === 400 || res.status === 404 || res.status === 409) {
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      return { state: 'refused', reason: body?.error ?? 'refused' };
    }
    if (!res.ok) return { state: 'error', status: res.status };
    const body = (await res.json().catch(() => null)) as {
      created?: unknown;
      mailed?: unknown;
    } | null;
    return { state: 'ok', created: body?.created === true, mailed: body?.mailed === true };
  } catch {
    return { state: 'error', status: null };
  }
}

/**
 * Connect, repoint or disconnect an organization's PSP account (#284).
 *
 * `null` disconnects — an organization without an account cannot take payments,
 * which is a real administrative state rather than a missing value. `undefined`
 * would mean «leave it alone», so the two are deliberately different here and on
 * the server.
 *
 * REFUSALS ARRIVE AS CODES, and two matter to whoever is typing:
 * `invalid_stripe_account` (not an `acct_…` — a truncated paste, or a secret key
 * in the wrong box) and `stripe_account_already_connected` (the account belongs to
 * another organization, which would pay one organization's revenue into the
 * other's bank account).
 */
/**
 * Connect, disconnect, or — with `replace` — repoint the organization's payment
 * account.
 *
 * `replace` is not a UI convenience: the server REFUSES to move a live account to a
 * different one without it (`stripe_account_replace_not_confirmed`), because from
 * the next payment onwards the money lands in another bank account and nothing
 * reports a problem. Send it only for an intent the operator confirmed against both
 * account values.
 */
export function setOrganizationStripeAccount(
  organizationId: string,
  stripeAccountId: string | null,
  options: { replace?: boolean } = {},
): Promise<WriteResult> {
  return write(`/platform/organizations/${encodeURIComponent(organizationId)}`, {
    method: 'PATCH',
    headers: JSON_HEADERS,
    body: JSON.stringify({
      stripeAccountId,
      ...(options.replace ? { replaceStripeAccount: true } : {}),
    }),
  });
}

export function revokeOrganizationRole(
  organizationId: string,
  userId: string,
  roleId: string,
): Promise<WriteResult> {
  return write(
    `/platform/organizations/${encodeURIComponent(organizationId)}/members/${encodeURIComponent(userId)}/roles/${encodeURIComponent(roleId)}`,
    { method: 'DELETE' },
  );
}

export interface LinkTerms {
  /** What the centre gives the club. */
  centreGrantsRoleId?: string | null;
  /** What the club gives the centre. */
  clubGrantsRoleId?: string | null;
  settlementModel?: 'centre_invoice' | 'direct';
  agreedCourtRateOre?: number | null;
  status?: 'active' | 'suspended';
}

export function upsertOrganizationLink(
  centreId: string,
  clubId: string,
  terms: LinkTerms,
): Promise<WriteResult> {
  return write(
    `/platform/organizations/${encodeURIComponent(centreId)}/links/${encodeURIComponent(clubId)}`,
    { method: 'PUT', headers: JSON_HEADERS, body: JSON.stringify(terms) },
  );
}

export function deleteOrganizationLink(centreId: string, clubId: string): Promise<WriteResult> {
  return write(
    `/platform/organizations/${encodeURIComponent(centreId)}/links/${encodeURIComponent(clubId)}`,
    { method: 'DELETE' },
  );
}
